/* eslint-disable prefer-destructuring */
/* eslint-disable no-shadow */
import React, { useEffect, useContext } from 'react'
import { useDispatch } from 'react-redux'
import trackError from '@helpers/trackError'
import { selectDynamicFilter } from '@store/redux/actions/filterActions'
import { initFit } from '@helpers/initFit'
import { setCookie, removeCookie } from '@helpers/session'
import {
  getAllAvailableProductOptionValues,
  createFilterCookies,
  getFiltersOptionsState
} from '@helpers/filters'
import {
  ORDERED_OPTIONS,
  ADDITIONAL_PROPERTIES,
  COOKIE_EXPIRE_3_MONTHS
} from '@consts/index'
import SavedFitBox from '@components/SavedFitBox'
import Notification from '@components/ui/Notification'
import VariantFilter from './VariantFilter'
import QuickLinks from './QuickLinks'
import styles from './styles.module.scss'
import ProductContext from '../context'
import { DYNAMIC_FILTER_OPTIONS_BY_PRODUCT_TYPE } from '@consts/index'
import { ProductOption, ProductWithVariants } from 'types/product'
import { FilterOption } from 'types/filters'
import htmr from 'htmr'

const OPTION_VALUES_EXCLUSIONS = [{ type: 'build', value: 'X' }]

function getSavedFitOptionsToShow(productOptions: ProductOption[]) {
  return productOptions
    .map(option => {
      const hasExcludedValues = option.values.some(value =>
        OPTION_VALUES_EXCLUSIONS.find(
          excludedValue =>
            option.type === excludedValue.type && value === excludedValue.value
        )
      )
      if (!hasExcludedValues) {
        return option.type
      }
      return null
    })
    .filter(Boolean)
}

function getListOfOptionsPositions(
  productOptions: ProductOption[],
  filteredSelectedOptions: FilterOption[]
) {
  return ORDERED_OPTIONS.reduce<string[]>((result, optionType) => {
    const normalizedOptionType = optionType.toLowerCase()
    const optionExists =
      productOptions.findIndex(f => f.type === normalizedOptionType) > -1

    if (optionExists) {
      result.push(normalizedOptionType)
    }

    return result
  }, []).reduce<number[]>((result, optionType, optionIndex) => {
    const optionSelected =
      filteredSelectedOptions.findIndex(f => f.type === optionType) > -1

    if (optionSelected) {
      result.push(optionIndex + 1)
    }

    return result
  }, [])
}

const transformOptionType = (
  option: FilterOption,
  productType: string
): string => {
  const isSizeOption = option.type === 'size'
  const isDynamicProductType = DYNAMIC_FILTER_OPTIONS_BY_PRODUCT_TYPE.includes(
    productType.toLowerCase()
  )

  return isSizeOption && isDynamicProductType
    ? 'ignoreSizeOptionType'
    : option.type
}

const getOptionsToSaveInCookies = (
  filterOptions: FilterOption[],
  productType: string
): FilterOption[] => {
  return filterOptions.filter(option => {
    const transformedType = transformOptionType(option, productType)
    const additionalProperty =
      ADDITIONAL_PROPERTIES[
        transformedType as keyof typeof ADDITIONAL_PROPERTIES
      ]
    const shouldSkipOption = additionalProperty?.skipInCookies
    return !shouldSkipOption
  })
}

function getMaxNumberOfOptionsToShow(listOfOptionsPositions: number[]) {
  return listOfOptionsPositions
    .sort((a, b) => (a > b ? 1 : -1))
    .reduce(
      (max, totalSelectedOptions) =>
        totalSelectedOptions === max + 1 ? totalSelectedOptions : max,
      0
    )
}

type FilterProps = {
  product: ProductWithVariants
  variantExist: boolean
  selectedOptions: FilterOption[]
  filterOptions: FilterOption[]
  isAbTestMadeToOrderOxfords: boolean
}

function Filter({
  product,
  variantExist,
  selectedOptions,
  filterOptions,
  isAbTestMadeToOrderOxfords
}: FilterProps) {
  const {
    options: productOptions,
    product_type: productType,
    product_feed: productFeed
  } = product
  const dispatch = useDispatch()
  const { discount } = useContext(ProductContext)

  const triggerSelectFilter = (payload: FilterOption[]) => {
    dispatch(selectDynamicFilter(payload))
  }

  useEffect(() => {
    if (filterOptions.length > 0) {
      return
    }

    try {
      const parsedCookie = initFit()
      const pushedFilterOptions = getFiltersOptionsState(parsedCookie || {})
      const defaultFilters = pushedFilterOptions.filter(
        pushedOption =>
          !filterOptions.some(
            filterOption =>
              filterOption.type === pushedOption.type &&
              filterOption.value.toLowerCase() ===
                pushedOption.value.toLowerCase()
          )
      )
      triggerSelectFilter(defaultFilters)
    } catch (e) {
      const errorMessage =
        e instanceof Error ? e.message : 'Filter initFit error'
      trackError(new Error(errorMessage))
    }
  }, [])

  useEffect(() => {
    const optionsToSaveInCookies = getOptionsToSaveInCookies(
      filterOptions,
      productType
    )
    const newFilters = createFilterCookies(optionsToSaveInCookies)
    setCookie('filters', newFilters, COOKIE_EXPIRE_3_MONTHS)
    removeCookie('th_ftv')
  }, [filterOptions])

  const listOfOptionsPositions = getListOfOptionsPositions(
    productOptions,
    selectedOptions
  )
  const maxNumberOfOptionsToShow = getMaxNumberOfOptionsToShow(
    listOfOptionsPositions
  )

  const savedFitOptionsToShow = getSavedFitOptionsToShow(productOptions)

  return (
    <div>
      {!!productOptions.length && (
        <div className={styles.SavedFitBox__wrapper}>
          <SavedFitBox
            optionTypesToShow={savedFitOptionsToShow}
            appliedFilterOptions={selectedOptions}
            showApplyButton={false}
          />
        </div>
      )}
      {productOptions.map((option, index) => (
        <VariantFilter
          name={option.name}
          type={option.type}
          selectedOptions={selectedOptions}
          open={index <= maxNumberOfOptionsToShow}
          position={option.position}
          variantExist={variantExist}
          values={option.values}
          productType={productType}
          optionsList={getAllAvailableProductOptionValues(
            product,
            selectedOptions,
            option.type
          )}
          key={`${option.type}`}
          selectFilter={triggerSelectFilter}
        />
      ))}
      {isAbTestMadeToOrderOxfords && (
        <Notification
          type="info"
          content="Our Oxford Shirts are made to order, so expect delivery within 2-3 weeks. Sit tight."
          classes={styles.filters__MessageBox}
        />
      )}
      {!!discount.description && !isAbTestMadeToOrderOxfords && (
        <Notification
          type="info"
          content={htmr(`${discount.description}`, {
            dangerouslySetChildren: ['br']
          })}
          classes={styles.filters__MessageBox}
        />
      )}
      <QuickLinks
        options={productOptions}
        type={productType}
        category={productFeed?.product_category || ''}
      />
    </div>
  )
}

export default Filter
